import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import axios from 'axios';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface TrustedTeam {
  _id: string;
  name: string;
  description: string;
  logoUrl: string;
  website: string;
  category?: {
    _id: string;
    name: string;
  } | null;
  featured: boolean;
  displayOrder: number;
  isActive: boolean;
}

const TrustedByTeamsDisplay: React.FC = () => {
  const [teams, setTeams] = useState<TrustedTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [allProjects, setAllProjects] = useState<any[]>([]);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await axios.get('/api/trusted-teams');
        if (response.data) {
          const teamsList = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data)
            ? response.data
            : [];
          const activeTeams = teamsList.filter((team: TrustedTeam) => team.isActive !== false);
          setTeams(activeTeams);

          // Extract unique categories
          const categoriesMap: { [key: string]: any } = {};
          activeTeams.forEach((team: TrustedTeam) => {
            if (team.category && !categoriesMap[team.category._id]) {
              categoriesMap[team.category._id] = team.category;
            }
          });
          setCategories(Object.values(categoriesMap));
        }

        // Fetch all projects for Services modal
        const projectsResponse = await axios.get('/api/projects?status=active&isPublished=true');
        if (projectsResponse.data.success) {
          setAllProjects(projectsResponse.data.data.projects);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, []);

  // Group teams by category
  const teamsByCategory = categories.map(category => ({
    category,
    teams: teams.filter(team => team.category?._id === category._id)
  }));

  return (
    <Layout
      showServicesModal={showServicesModal}
      setShowServicesModal={setShowServicesModal}
      currentPage="other"
      projects={allProjects}
    >
      <section className="max-w-7xl mx-auto px-6 py-20">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-gray-400 text-lg">Loading...</div>
          </div>
        ) : (
          <div className="space-y-16">
            {teamsByCategory.map(({ category, teams: categoryTeams }) => (
              <div key={category._id}>
                {/* Category Header */}
                <div className="text-center mb-12">
                  <h1 className="text-2xl md:text-3xl font-bold text-white uppercase tracking-wider mb-4">
                    {category.name}
                  </h1>
                </div>

                {/* Teams Grid */}
                <div className="grid grid-cols-5 gap-6">
                  {categoryTeams.map((team) => (
                    <a
                      key={team._id}
                      href={team.website || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <div className="dashboard-block p-6 transition-all duration-300 hover:scale-105 flex flex-col items-center text-center min-h-[160px] card-hover">
                        {team.logoUrl && (
                          <div className="w-20 h-20 rounded-xl overflow-hidden mb-4 flex items-center justify-center">
                            <img
                              src={getFullImageUrl(team.logoUrl)}
                              alt={team.name}
                              className="max-w-full max-h-full object-contain"
                              onError={handleImageError}
                            />
                          </div>
                        )}
                        <span className="text-white text-sm font-semibold group-hover:text-indigo-400 transition-colors leading-tight uppercase">
                          {team.name}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default TrustedByTeamsDisplay;
